(() => {
  const TAG = 'halden-cart-discount-progress-v4';

  if (!customElements.get(TAG)) {
    class HaldenCartDiscountProgressV4 extends HTMLElement {
      connectedCallback() {
        if (this.dataset.progressInitialized === 'true') return;
        this.dataset.progressInitialized = 'true';

        this.tiers = Array.from(this.querySelectorAll('[data-progress-tier]'));
        this.fill = this.querySelector('[data-progress-fill]');
        this.status = this.querySelector('[data-progress-status]');
        this.titleEl = this.querySelector('[data-progress-title]');
        this.thresholds = this.tiers.map((tier) => Math.max(1, Number(tier.dataset.threshold) || 1));
        this.cartUpdateUnsubscriber = null;
        this.retryTimer = null;
        this.fetchTimer = null;
        this.cartRevision = 0;
        this.fetchSequence = 0;

        this.update(Number(this.dataset.cartCount) || 0);

        this.handleCartUpdate = (event) => {
          this.cartRevision += 1;
          const cart = event?.cartData;
          if (cart && Array.isArray(cart.items) && Number.isFinite(Number(cart.item_count))) {
            this.update(this.getEligibleItemCount(cart));
          }
          // Fetch after the cart mutation settles; event payloads can be partial.
          this.scheduleFetch(300);
        };

        this.onDocumentCartUpdated = (event) => {
          this.cartRevision += 1;
          const cart = event?.detail?.cart;
          if (cart && Array.isArray(cart.items)) {
            this.update(this.getEligibleItemCount(cart));
          }
          // Fetch after the cart mutation settles; event payloads can be partial.
          this.scheduleFetch(300);
        };

        document.addEventListener('cart:updated', this.onDocumentCartUpdated);
        document.addEventListener('cart:refresh', this.onDocumentCartUpdated);
        this.attachPubSub();

        // Final source-of-truth sync. This also covers app/custom cart changes
        // that do not publish Dawn's pub/sub event.
        this.scheduleFetch(60);
      }

      disconnectedCallback() {
        document.removeEventListener('cart:updated', this.onDocumentCartUpdated);
        document.removeEventListener('cart:refresh', this.onDocumentCartUpdated);
        if (typeof this.cartUpdateUnsubscriber === 'function') this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
        window.clearInterval(this.retryTimer);
        window.clearTimeout(this.fetchTimer);
        this.dataset.progressInitialized = 'false';
        this.fetchSequence += 1;
      }

      attachPubSub() {
        const trySubscribe = () => {
          if (typeof this.cartUpdateUnsubscriber === 'function') return true;
          if (
            typeof subscribe === 'function' &&
            typeof PUB_SUB_EVENTS !== 'undefined' &&
            PUB_SUB_EVENTS.cartUpdate
          ) {
            this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
            return true;
          }
          return false;
        };

        if (trySubscribe()) return;

        let attempts = 0;
        this.retryTimer = window.setInterval(() => {
          attempts += 1;
          if (trySubscribe() || attempts >= 24) window.clearInterval(this.retryTimer);
        }, 250);
      }

      scheduleFetch(delay = 0) {
        window.clearTimeout(this.fetchTimer);
        this.fetchTimer = window.setTimeout(() => this.fetchCartCount(), delay);
      }

      async fetchCartCount() {
        const revision = this.cartRevision;
        const sequence = ++this.fetchSequence;
        try {
          const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
          const response = await fetch(`${cartUrl}.js`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            cache: 'no-store',
          });
          if (!response.ok) return;
          const cart = await response.json();
          if (!this.isConnected || revision !== this.cartRevision || sequence !== this.fetchSequence) return;
          this.update(this.getEligibleItemCount(cart));
        } catch (error) {
          console.warn('HALDEN cart discount progress could not refresh cart count.', error);
        }
      }

      getEligibleItemCount(cart) {
        if (!cart || !Array.isArray(cart.items)) return 0;
        return cart.items.reduce((total, item) => {
          if (item?.properties?._halden_gift === 'true') return total;
          return total + (Number(item.quantity) || 0);
        }, 0);
      }

      update(quantity) {
        const count = Math.max(0, Number(quantity) || 0);
        this.dataset.cartCount = String(count);
        this.hidden = !this.tiers.length || (count < 1 && this.dataset.designPreview !== 'true');

        this.tiers.forEach((tier) => {
          const threshold = Math.max(1, Number(tier.dataset.threshold) || 1);
          tier.classList.toggle('is-unlocked', count >= threshold);
        });

        const reached = this.thresholds.filter((threshold) => count >= threshold).length;
        const segments = this.tiers.length - 1;
        const fillPercent = segments > 0 ? Math.max(0, (reached - 1) / segments * 100) : 0;
        if (this.fill) this.fill.style.width = `${fillPercent}%`;

        if (this.titleEl) {
          const messageKeys = ['messageZero', 'messageOne', 'messageTwo', 'messageThree', 'messageFour'];
          const index = Math.min(4, count);
          let message = '';
          for (let tier = index; tier >= 0; tier -= 1) {
            const configured = (this.dataset[messageKeys[tier]] || '').trim();
            if (configured) {
              message = configured;
              break;
            }
          }
          this.titleEl.textContent = message;
        }

        if (this.status) {
          this.status.textContent = `${reached} of ${this.tiers.length} configured rewards unlocked.`;
        }
      }
    }

    customElements.define(TAG, HaldenCartDiscountProgressV4);
  }
})();
